/**
 * Exercise Card Component
 *
 * Displays exercise with completed sets
 * Large touch targets for mobile
 * Intensity displayed as 1-5 stars
 * Click title to rename exercise
 * Click set values to edit weight/reps
 * X button to delete sets
 */

import { useState, useCallback, type KeyboardEvent } from 'react';
import './ExerciseCard.css';

interface WorkoutSet {
  id: string;
  set_number: number;
  reps: number | null;
  weight: number | null;
  intensity: number | null;
}

interface ExerciseCardProps {
  exerciseName: string;
  sets: WorkoutSet[];
  onAddSet: () => void;
  onRename?: (oldName: string, newName: string) => void;
  onUpdateSet?: (setId: string, reps?: number, weight?: number, intensity?: number) => void;
  onDeleteSet?: (setId: string) => void;
}

export function ExerciseCard({
  exerciseName,
  sets,
  onAddSet,
  onRename,
  onUpdateSet,
  onDeleteSet,
}: ExerciseCardProps): JSX.Element {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(exerciseName);
  const [editingSetId, setEditingSetId] = useState<string | null>(null);
  const [editWeight, setEditWeight] = useState('');
  const [editReps, setEditReps] = useState('');

  const renderStars = (intensity: number): string => {
    return '⭐'.repeat(intensity);
  };

  const handleSaveRename = useCallback(() => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== exerciseName && onRename) {
      onRename(exerciseName, trimmed);
    }
    setIsEditing(false);
  }, [editName, exerciseName, onRename]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleSaveRename();
      } else if (e.key === 'Escape') {
        setEditName(exerciseName);
        setIsEditing(false);
      }
    },
    [handleSaveRename, exerciseName]
  );

  const handleStartEditSet = useCallback((set: WorkoutSet) => {
    setEditingSetId(set.id);
    setEditWeight(set.weight?.toString() ?? '');
    setEditReps(set.reps?.toString() ?? '');
  }, []);

  const handleSaveSetEdit = useCallback(() => {
    if (editingSetId && onUpdateSet) {
      const weight = editWeight ? parseFloat(editWeight) : undefined;
      const reps = editReps ? parseInt(editReps, 10) : undefined;
      const set = sets.find((s) => s.id === editingSetId);
      onUpdateSet(editingSetId, reps, weight, set?.intensity ?? undefined);
    }
    setEditingSetId(null);
  }, [editingSetId, editWeight, editReps, sets, onUpdateSet]);

  const handleSetKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleSaveSetEdit();
      } else if (e.key === 'Escape') {
        setEditingSetId(null);
      }
    },
    [handleSaveSetEdit]
  );

  return (
    <div className="exercise-card">
      <div className="exercise-card__header">
        {isEditing ? (
          <input
            type="text"
            className="exercise-card__title-input"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleSaveRename}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        ) : (
          <h3
            className="exercise-card__title"
            onClick={() => {
              if (onRename) {
                setEditName(exerciseName);
                setIsEditing(true);
              }
            }}
            title={onRename ? 'Click to rename' : undefined}
            style={{ cursor: onRename ? 'pointer' : 'default' }}
          >
            {exerciseName}
          </h3>
        )}
        <div className="exercise-card__badge">{sets.length} sets</div>
      </div>

      <div className="exercise-card__sets">
        {sets.map((set) => (
          <div key={set.id} className="exercise-card__set">
            <div className="exercise-card__set-number">Set {set.set_number}</div>
            <div className="exercise-card__set-data">
              {editingSetId === set.id ? (
                <>
                  <input
                    type="number"
                    className="exercise-card__set-input"
                    value={editWeight}
                    onChange={(e) => setEditWeight(e.target.value)}
                    onKeyDown={handleSetKeyDown}
                    placeholder="lbs"
                    autoFocus
                  />
                  <span className="exercise-card__set-separator">×</span>
                  <input
                    type="number"
                    className="exercise-card__set-input"
                    value={editReps}
                    onChange={(e) => setEditReps(e.target.value)}
                    onKeyDown={handleSetKeyDown}
                    onBlur={handleSaveSetEdit}
                    placeholder="reps"
                  />
                </>
              ) : (
                <>
                  {set.weight !== null && (
                    <span
                      className="exercise-card__set-value exercise-card__set-value--editable"
                      onClick={() => onUpdateSet && handleStartEditSet(set)}
                      title={onUpdateSet ? 'Click to edit' : undefined}
                    >
                      {set.weight} lbs
                    </span>
                  )}
                  {set.weight !== null && set.reps !== null && (
                    <span className="exercise-card__set-separator">×</span>
                  )}
                  {set.reps !== null && (
                    <span
                      className="exercise-card__set-value exercise-card__set-value--editable"
                      onClick={() => onUpdateSet && handleStartEditSet(set)}
                      title={onUpdateSet ? 'Click to edit' : undefined}
                    >
                      {set.reps} reps
                    </span>
                  )}
                  {set.reps === null && set.weight === null && (
                    <span
                      className="exercise-card__set-value exercise-card__set-value--editable"
                      onClick={() => onUpdateSet && handleStartEditSet(set)}
                    >
                      Completed
                    </span>
                  )}
                  {set.intensity && (
                    <span className="exercise-card__set-intensity">{renderStars(set.intensity)}</span>
                  )}
                </>
              )}
            </div>
            {onDeleteSet && (
              <button
                type="button"
                className="exercise-card__set-delete"
                onClick={() => onDeleteSet(set.id)}
                title="Delete set"
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>

      <button type="button" className="exercise-card__add-set" onClick={onAddSet}>
        Add Set
      </button>
    </div>
  );
}
